import * as React from "react";
import { Button, Modal } from "react-bootstrap";
import { strategyGroupCategoryLabels, strategyTargetGroupLabels } from "../../../../../../label/activity";
import { makeAgeLabel, makePriceLabel } from "../../../../../../server/lib/concept/common/helpers";
import {
	BeautyType,
	StrategyGroup,
	Married,
	Children,
	Occupation,
	Fragrance,
	getChannelEnum,
	SensitiveSkin,
	FoundationType,
	FacewashFunction,
	getSkinAttributeEnum,
	SensitiveSkinFrequencyRank,
	SkinProblemFrequency,
	AestheticMedicineExperience,
} from "../../../../../../server/models/activity";
import { IndentArea } from "../../../pages/report/outline";
import { GroupPanel } from "./group-panel";
import { GypsyGroupPanel } from "./gypsy-group-panel";

const SCategoryRow = React.memo((props: { label: string; value: string }) => {
	const { label, value } = props;
	return (
		<>
			<div>
				・{label}：{value}{" "}
			</div>
		</>
	);
});

export type StrategyConditionsProps = {
	version?: string;
	strategyTargetGroup: StrategyGroup;
};

export const StrategyConditions = React.memo((props: StrategyConditionsProps) => {
	const { strategyTargetGroup, version } = props;
	const [show, setShow] = React.useState<boolean>(false);
	const onClose = React.useCallback(() => setShow(false), []);
	const onOpen = React.useCallback(() => setShow(true), []);
	const chanelEnum = React.useMemo(() => getChannelEnum(version), [version]);
	return (
		<>
			<Button onClick={onOpen} variant={"dark"} size={"sm"}>
				戦略ターゲット詳細
			</Button>
			<Modal show={show} onHide={onClose}>
				<Modal.Header>
					<Modal.Title>戦略ターゲット詳細</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{/*　年齢 */}
					<GroupPanel label={strategyTargetGroupLabels.age} value={makeAgeLabel(strategyTargetGroup.age)} />
					{/*　未既婚 */}
					{strategyTargetGroup.married && !!strategyTargetGroup.married.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.married}
							value={strategyTargetGroup.married.map((target) => Married[target]).join("、")}
						/>
					)}
					{/* 子ども年齢（同居） */}
					{strategyTargetGroup.children && !!strategyTargetGroup.children.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.children}
							value={strategyTargetGroup.children.map((target) => Children[target]).join("、")}
						/>
					)}
					{/* 職業 */}
					{strategyTargetGroup.occupation && !!strategyTargetGroup.occupation.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.occupation}
							value={strategyTargetGroup.occupation.map((target) => Occupation[target]).join("、")}
						/>
					)}
					{/* 肌悩み */}
					{strategyTargetGroup.skinAttribute && !!strategyTargetGroup.skinAttribute.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.skinAttribute}
							value={strategyTargetGroup.skinAttribute
								.map((target) => getSkinAttributeEnum(version)[target as any])
								.join("、")}
						/>
					)}
					{/* 敏感肌① */}
					{strategyTargetGroup.sensitiveSkin && !!strategyTargetGroup.sensitiveSkin.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.sensitiveSkin}
							value={strategyTargetGroup.sensitiveSkin.map((target) => SensitiveSkin[target]).join("、")}
						/>
					)}
					{/* 香料：使わない */}
					{strategyTargetGroup.fragrance && !!strategyTargetGroup.fragrance.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.fragrance}
							value={strategyTargetGroup.fragrance.map((target) => Fragrance[target]).join("、")}
						/>
					)}
					{/* 敏感肌② */}
					{/* 敏感肌頻度 */}
					{strategyTargetGroup.sensitiveSkinFrequency && !!strategyTargetGroup.sensitiveSkinFrequency.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.sensitiveSkinFrequency ?? ""}
							value={strategyTargetGroup.sensitiveSkinFrequency
								.map((target) => SensitiveSkinFrequencyRank[target])
								.join("、")}
						/>
					)}
					{/* 肌トラブル頻度 */}
					{strategyTargetGroup.skinProblemFrequency && !!strategyTargetGroup.skinProblemFrequency.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.skinProblemFrequency ?? ""}
							value={strategyTargetGroup.skinProblemFrequency.map((target) => SkinProblemFrequency[target]).join("、")}
						/>
					)}
					{/* ファンデーションタイプ */}
					{strategyTargetGroup.foundationType && !!strategyTargetGroup.foundationType.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.foundationType}
							value={strategyTargetGroup.foundationType.map((target) => FoundationType[target]).join("、")}
						/>
					)}
					{/* 洗顔機能 */}
					{!!strategyTargetGroup.facewashFunction && (
						<GroupPanel
							label={strategyTargetGroupLabels.facewashFunction}
							value={FacewashFunction[strategyTargetGroup.facewashFunction].toString()}
						/>
					)}
					{/* 購入チャネル */}
					{strategyTargetGroup.channel && !!strategyTargetGroup.channel.length && (
						<GroupPanel
							label={strategyTargetGroupLabels.channel}
							value={strategyTargetGroup.channel.map((target) => chanelEnum[target as any]).join("、")}
						/>
					)}
					{/* ジプシー */}
					{strategyTargetGroup.gypsy && <GypsyGroupPanel gypsy={strategyTargetGroup.gypsy} />}
					{/* 美容医療利用経験 */}
					{strategyTargetGroup.aestheticMedicineExperience &&
						!!strategyTargetGroup.aestheticMedicineExperience.length && (
							<GroupPanel
								label={strategyTargetGroupLabels.aestheticMedicineExperience ?? ""}
								value={strategyTargetGroup.aestheticMedicineExperience
									.map((target) => AestheticMedicineExperience[target])
									.join("、")}
							/>
						)}
					<hr />
					<div>カテゴリ条件</div>
					<div>以下カテゴリ間の条件を{strategyTargetGroup.categoryOrAnd}条件で設定します。</div>
					{/* <div>以下カテゴリごとに{CategoryOrAnd[strategyTargetGroup.categoryOrAnd]}となります。</div> */}
					<hr />
					{strategyTargetGroup.category.map((cate) => {
						const surveyChoice = cate.category.surveyChoice.find((sc) => sc.value === cate.surveyChoice);
						return (
							<React.Fragment key={`category-${surveyChoice.value}`}>
								<div>{surveyChoice.label}</div>
								<IndentArea indent={1}>
									{/* 価格帯（税抜き） */}
									{cate.price && (
										<SCategoryRow label={strategyGroupCategoryLabels.price} value={makePriceLabel(cate.price)} />
									)}
									{/* カテゴリタイプ（スキンケアのみ） */}
									{/* 美類 */}
									{cate.beautyType && !!cate.beautyType.length && (
										<SCategoryRow
											label={strategyGroupCategoryLabels.beautyType}
											value={cate.beautyType.map((target) => BeautyType[target]).join("、")}
										/>
									)}
									{/* 過去1年購入＆使用ブランド */}
									{cate.usedBrand && !!cate.usedBrand.length && (
										<SCategoryRow label={strategyGroupCategoryLabels.usedBrand} value={cate.usedBrand.join("、")} />
									)}
									{/* 過去1年非購入ブランド */}
									{cate.unusedBrand && !!cate.unusedBrand.length && (
										<SCategoryRow label={strategyGroupCategoryLabels.unusedBrand} value={cate.unusedBrand.join("、")} />
									)}
								</IndentArea>
							</React.Fragment>
						);
					})}
					{strategyTargetGroup.nonPurchaseCategoryConfig && (
						<>
							<hr />

							<div>{strategyTargetGroupLabels.nonPurchaseCategoryConfig}</div>

							<div>
								以下カテゴリ間の条件を{strategyTargetGroup.nonPurchaseCategoryConfig.categoryOrAnd}条件で設定します。
							</div>

							<hr />

							{strategyTargetGroup.nonPurchaseCategoryConfig.surveyChoices.map((x) => (
								<div key={x.value}>{x.label}</div>
							))}
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={onClose} variant={"outline-secondary"}>
						close
					</Button>
				</Modal.Footer>
			</Modal>
			<IndentArea indent={0.7}>
				<div>
					{strategyTargetGroupLabels.age}：{makeAgeLabel(strategyTargetGroup.age)}
				</div>
				{strategyTargetGroup.category ? (
					<>
						<div>{strategyTargetGroupLabels.category}：</div>
						<IndentArea indent={0.7}>
							{strategyTargetGroup.category.map((c) => {
								const surveyChoie = c.category.surveyChoice.find((sc) => sc.value === c.surveyChoice);
								const label =
									c.category.type === "liquid-foundation" &&
									strategyTargetGroup.foundationType &&
									strategyTargetGroup.foundationType.length
										? strategyTargetGroup.foundationType
												.map((target) => FoundationType[target].toString().replace("　タイプ", ""))
												.join("・") + "　ファンデーション"
										: surveyChoie.label;
								return <div key={`strategy-category-${surveyChoie.value}`}>{label}</div>;
							})}
						</IndentArea>
					</>
				) : (
					""
				)}
			</IndentArea>
		</>
	);
});
